import {
  Text,
  Box,
  Collapse,
  Checkbox,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Tooltip,
} from "@chakra-ui/react";

import {
  IconOnline,
  IconTelephone,
  IconLocationPin,
  IconAlertCircle,
  IconEmail,
} from "../../../../../styles/icons";
import { InfoIcon } from "@chakra-ui/icons";
import React, { useState, forwardRef, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  profileAccordionButton,
  profileAccordionPanel,
} from "../../../../utilities/render-helpers/renderPractitionerProfile";
import { Field, Input, Label } from "@headlessui/react";
import AddressSearch from "react-loqate";
import { FaHome } from "react-icons/fa";
import { MdOutlineAddCircleOutline, MdDelete } from "react-icons/md";
import { updateProfileValue } from "../../../../redux/actions/user";

const AddressSearchInput = forwardRef(({ ...props }, ref) => (
  <Field
    className={`flex items-center bg-gray-50 ${props.value === "" ? "rounded" : "rounded-t"} overflow-hidden`}
  >
    <Label className="pl-4">
      <FaHome />
    </Label>
    <Input
      ref={ref}
      {...props}
      className="flex-1 w-full appearance-none border-none bg-transparent"
      type="text"
      placeholder="Search for your address"
      autocomplete="chrome-off"
    />
  </Field>
));
AddressSearchInput.displayName = "AddressSearchInput";

const AddressList = forwardRef((props, ref) => (
  <ul
    {...props}
    className="bg-white border-b border-l border-r p-2 rounded-b z-[1000]"
    ref={ref}
  />
));
AddressList.displayName = "AddressList";

export const searchListComponents = {
  Input: AddressSearchInput,
  List: AddressList,
  ListItem: ({ suggestion, ...props }) => (
    <li
      onKeyDown={(e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          const next = e.target.nextSibling;
          if (next) {
            next.focus();
          }
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          const previous = e.taget.previousSibling;
          if (previous) {
            previous.focus();
          }
        }
      }}
      {...props}
      className="p-2 hover:bg-gray-50 cursor-pointer active:bg-gray-50 active:outline-blue-300 rounded"
    >
      {`${suggestion.Text} ${suggestion.Description}`}
    </li>
  ),
};

const EditSectionSessionType = ({ profile }) => {
  const [contactOnline, setContactOnline] = useState(profile.contactOnline);
  const [contactPhone, setContactPhone] = useState(profile.contactPhone);
  const [secondLocation, setSecondLocation] = useState(
    profile.addresses.length > 1
  );

  const dispatch = useDispatch();
  const [primaryAddress, setPrimaryAddress] = useState(
    profile.addresses.length > 0
      ? {
          Line1: profile.addresses[0].address,
          Line2: "",
          Line3: "",
          City: profile.addresses[0].city,
          PostalCode: profile.addresses[0].postcode,
        }
      : null
  );
  const [secondaryAddress, setSecondaryAddress] = useState(
    profile.addresses.length > 1
      ? {
          Line1: profile.addresses[1].address,
          Line2: "",
          Line3: "",
          City: profile.addresses[1].city,
          PostalCode: profile.addresses[1].postcode,
        }
      : null
  );
  const [primaryLocation, setPrimaryLocation] = useState(
    profile.addresses.length > 0 ? profile.addresses[0].location : null
  );
  const [secondaryLocation, setSecondaryLocation] = useState(
    profile.addresses.length > 1 ? profile.addresses[1].location : null
  );
  const [primaryDisabled, setPrimaryDisabled] = useState(
    profile.addresses.length > 0 ? profile.addresses[0].disabledFriendly : false
  );
  const [secondaryDisabled, setSecondaryDisabled] = useState(
    profile.addresses.length > 1 ? profile.addresses[1].disabledFriendly : false
  );
  const [selectedSessionTypes, setSelectedSessionTypes] = useState(
    profile.sessionTypes.map((type) => ({ ...type, selected: true }))
  );

  const handleProfileValueUpdate = useCallback(
    (key, value, isAddress = false, isPrimary = true) => {
      if (isAddress) {
        let newAddresses = [];
        if (isPrimary) {
          newAddresses.push({
            address: `${value.address.Line1}${value.address.Line2 !== "" ? `, ${value.address.Line2}` : ""}`,
            postcode: value.address.PostalCode,
            city: value.address.City,
            location: value.location,
            disabledFriendly: primaryDisabled,
          });
          setPrimaryLocation(value.location);
        } else if (primaryAddress) {
          newAddresses.push({
            address: `${primaryAddress.Line1}${primaryAddress.Line2 !== "" ? `, ${primaryAddress.Line2}` : ""}`,
            postcode: primaryAddress.PostalCode,
            city: primaryAddress.City,
            location: primaryLocation,
            disabledFriendly: primaryDisabled,
          });
        }

        if (secondaryAddress && secondLocation && isPrimary) {
          newAddresses.push({
            address: `${secondaryAddress.Line1}${secondaryAddress.Line2 !== "" ? `, ${secondaryAddress.Line2}` : ""}`,
            postcode: secondaryAddress.PostalCode,
            city: secondaryAddress.City,
            location: secondaryLocation,
            disabledFriendly: secondaryDisabled,
          });
        }

        if (!isPrimary && value !== null) {
          newAddresses.push({
            address: `${value.address.Line1}${value.address.Line2 !== "" ? `, ${value.address.Line2}` : ""}`,
            postcode: value.address.PostalCode,
            city: value.address.City,
            location: value.location,
            disabledFriendly: secondaryDisabled,
          });
          setSecondaryLocation(value.location);
        }
        dispatch(updateProfileValue("addresses", newAddresses));
        dispatch(updateProfileValue("sessionTypes", selectedSessionTypes));
      } else if (key === "disabled") {
        const newAddresses = [
          {
            address: `${primaryAddress.Line1}${primaryAddress.Line2 !== "" ? `, ${primaryAddress.Line2}` : ""}`,
            postcode: primaryAddress.PostalCode,
            city: primaryAddress.City,
            location: primaryLocation,
            disabledFriendly: isPrimary ? value : primaryDisabled,
          },
        ];

        if (secondaryLocation) {
          newAddresses.push({
            address: `${secondaryAddress.Line1}${secondaryAddress.Line2 !== "" ? `, ${secondaryAddress.Line2}` : ""}`,
            postcode: secondaryAddress.PostalCode,
            city: secondaryAddress.City,
            location: secondaryLocation,
            disabledFriendly: isPrimary ? secondaryDisabled : value,
          });
        }

        if (isPrimary) {
          setPrimaryDisabled(value);
        } else {
          setSecondaryDisabled(value);
        }

        dispatch(updateProfileValue("addresses", newAddresses));
      } else if (key === "sessionTypes") {
        let newSessionTypes;
        if (value.selected) {
          newSessionTypes = selectedSessionTypes.concat(value);
        } else {
          newSessionTypes = selectedSessionTypes.filter(
            (type) => type.value !== value.value
          );
        }

        setSelectedSessionTypes(newSessionTypes);
        dispatch(updateProfileValue("sessionTypes", newSessionTypes));
      } else {
        dispatch(updateProfileValue(key, value));
      }
    },
    [
      secondaryAddress,
      secondLocation,
      dispatch,
      selectedSessionTypes,
      primaryDisabled,
      primaryAddress,
      primaryLocation,
      secondaryLocation,
      secondaryDisabled,
    ]
  );

  const loqateSearchAddress = async (search, isPrimary = true) => {
    let buildUrl =
      "https://api.addressy.com/Geocoding/International/Geocode/v1.10/json3.ws";
    buildUrl += `?Key=${encodeURIComponent(process.env.NEXT_PUBLIC_LOQATE_APIKEY)}`;
    buildUrl += "&Country=gb";
    buildUrl += `&Location=${encodeURIComponent(`${search.Line1} ${search.City} ${search.PostalCode}`)}`;
    const res = await fetch(buildUrl, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
    });
    const body = await res.json();
    if (body.Items.length === 0) {
      return;
    }

    handleProfileValueUpdate(
      "addresses",
      {
        address: search,
        location: {
          lng: body.Items[0].Longitude,
          lat: body.Items[0].Latitude,
        },
      },
      true,
      isPrimary
    );
  };

  const phoneChecked = useMemo(
    () =>
      selectedSessionTypes.filter((type) => type.value === 2 && type.selected)
        .length > 0,
    [selectedSessionTypes]
  );
  const onlineChecked = useMemo(
    () =>
      selectedSessionTypes.filter((type) => type.value === 3 && type.selected)
        .length > 0,
    [selectedSessionTypes]
  );
  const inPersonChecked = useMemo(
    () =>
      selectedSessionTypes.filter((type) => type.value === 1 && type.selected)
        .length > 0,
    [selectedSessionTypes]
  );
  const emailChecked = useMemo(
    () =>
      selectedSessionTypes.filter((type) => type.value === 4 && type.selected)
        .length > 0,
    [selectedSessionTypes]
  );

  // Render
  return (
    <AccordionItem>
      <h2>
        <AccordionButton _expanded={profileAccordionButton}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flex="1"
            //width="100%"
            textAlign="left"
          >
            <Box>
              <Text>Session hosting</Text>
              <Text textStyle="footer2" color="brand.200">
                {profile.sessionTypes.map((elem) => elem.label).join(" | ")}
              </Text>
            </Box>
            {profile.sessionTypes?.length > 0 ? (
              <Text padding="8px" textStyle="body2" color="green">
                Complete
              </Text>
            ) : (
              <Box display="flex" alignItems="center" padding="8px">
                <IconAlertCircle size="20px" color="tomato" />
                <Text textStyle="body2" paddingLeft="8px">
                  Show how you host your sessions
                </Text>
              </Box>
            )}
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel sx={profileAccordionPanel}>
        <div className="flex flex-col rounded items-start gap-6 w-full bg-white p-4">
          <div className="w-full">
            <h3 className="text-lg text-slate-600 font-bold pb-2 flex items-center">
              How do you offer your sessions? Select all options that apply
              <p className="text-red-500 superscript text-sm pl-1">*</p>
            </h3>
          </div>
          <div
            className={`w-full p-4 transition rounded ${phoneChecked ? "bg-green-50" : "bg-gray-100"}`}
          >
            <Checkbox
              isChecked={phoneChecked}
              colorScheme="green"
              onChange={(e) => {
                handleProfileValueUpdate("sessionTypes", {
                  value: 2,
                  label: "By phone",
                  selected: e.target.checked,
                });
              }}
            >
              <p
                className={`transition ${phoneChecked ? "text-slate-800" : "text-slate-600"}`}
              >
                By phone
              </p>
            </Checkbox>
            <Collapse in={phoneChecked} animateOpacity width="100%">
              <div>
                <h5 className="font-light text-sm my-2 flex items-center text-slate-700">
                  When you make a booking with a client, you can send your phone
                  number or you can add it below and we will pre-fill for you.
                </h5>

                <Field className="flex items-center bg-white rounded overflow-hidden">
                  <Label className="pl-4">
                    <IconTelephone />
                  </Label>
                  <Input
                    className="flex-1 w-full appearance-none border-none"
                    type="tel"
                    placeholder="Phone number"
                    onChange={(e) => {
                      handleProfileValueUpdate("contactPhone", e.target.value);
                      setContactPhone(e.target.value);
                    }}
                    value={contactPhone}
                  />
                </Field>
              </div>
            </Collapse>
          </div>
          <div
            className={`w-full p-4 transition rounded ${onlineChecked ? "bg-green-50" : "bg-gray-100"}`}
          >
            <Checkbox
              isChecked={onlineChecked}
              colorScheme="green"
              onChange={(e) => {
                handleProfileValueUpdate("sessionTypes", {
                  value: 3,
                  label: "Online",
                  selected: e.target.checked,
                });
              }}
            >
              <p
                className={`transition ${onlineChecked ? "text-slate-800" : "text-slate-600"}`}
              >
                Online
              </p>
            </Checkbox>
            <Collapse in={onlineChecked} animateOpacity width="100%">
              <div>
                <h5 className="font-light text-sm my-2 flex items-center text-slate-700">
                  When you make a booking with a client, you can add your online
                  video link or you can add it below and we will pre-fill for
                  you.
                </h5>

                <Field className="flex items-center bg-white rounded overflow-hidden">
                  <Label className="pl-4">
                    <IconOnline />
                  </Label>
                  <Input
                    className="flex-1 w-full appearance-none border-none"
                    type="text"
                    placeholder="Zoom/Teams link"
                    onChange={(e) => {
                      handleProfileValueUpdate("contactOnline", e.target.value);
                      setContactOnline(e.target.value);
                    }}
                    value={contactOnline}
                  />
                </Field>
              </div>
            </Collapse>
          </div>
          <div
            className={`w-full p-4 transition rounded ${inPersonChecked ? "bg-green-50" : "bg-gray-100"}`}
          >
            <Checkbox
              isChecked={inPersonChecked}
              colorScheme="green"
              onChange={(e) => {
                handleProfileValueUpdate("sessionTypes", {
                  value: 1,
                  label: "In person",
                  selected: e.target.checked,
                });
              }}
            >
              <p
                className={`transition ${inPersonChecked ? "text-slate-800" : "text-slate-600"}`}
              >
                In person
              </p>
            </Checkbox>
            <Collapse in={inPersonChecked} animateOpacity width="100%">
              <div>
                <h5 className="font-light text-sm my-2 flex items-center text-slate-700">
                  Location and proximity is a popular client search. Please add
                  (up to 2) locations in which you are available for in person
                  meetings.
                  <p className="text-red-500 superscript text-sm pl-1">*</p>
                </h5>
                <div className="flex flex-col gap-2">
                  <div className="bg-white p-2 flex flex-col rounded">
                    <div className="flex items-center gap-2 border-b pb-2 mb-2">
                      <IconLocationPin size="20px" />
                      <p className="">Primary location</p>
                    </div>
                    <AddressSearch
                      locale="en-GB"
                      apiKey={process.env.NEXT_PUBLIC_LOQATE_APIKEY}
                      onSelect={(address) => {
                        setPrimaryAddress(address);
                        loqateSearchAddress(address, true);
                      }}
                      countries={["GB", "GG", "IE", "IM", "JE"]}
                      components={searchListComponents}
                    />
                    {primaryAddress && (
                      <div className="flex flex-col gap-1 py-2 border-b">
                        <div className='px-2 pb-1'>
                          <span className='text-brand-alt text-sm'>
                            You can edit the text of your location below. This will not change the actual location in search. To update the location in search, please search for the address in the box above
                          </span>
                        </div>
                        <Input
                          className="p-2 rounded bg-gray-50 border-0"
                          value={primaryAddress.Line1}
                          onChange={(e) => {
                            setPrimaryAddress({
                              ...primaryAddress,
                              Line1: e.target.value,
                            });
                            handleProfileValueUpdate(
                              "addresses",
                              {
                                location: {
                                  ...primaryLocation,
                                },
                                address: {
                                  ...primaryAddress,
                                  Line1: e.target.value,
                                },
                              },
                              true,
                              true
                            );
                          }}
                        />
                        {primaryAddress.Line2 && (
                          <Input
                            className="p-2 rounded bg-gray-50 border-0"
                            value={primaryAddress.Line2}
                            onChange={(e) => {
                              setPrimaryAddress({
                                ...primaryAddress,
                                Line2: e.target.value,
                              });
                              handleProfileValueUpdate(
                                "addresses",
                                {
                                  location: {
                                    ...primaryLocation,
                                  },
                                  address: {
                                    ...primaryAddress,
                                    Line2: e.target.value,
                                  },
                                },
                                true,
                                true
                              );
                            }}
                          />
                        )}
                        {primaryAddress.Line3 && (
                          <Input
                            className="p-2 rounded bg-gray-50 border-0"
                            value={primaryAddress.Line3}
                            onChange={(e) => {
                              setPrimaryAddress({
                                ...primaryAddress,
                                Line3: e.target.value,
                              });
                              handleProfileValueUpdate(
                                "addresses",
                                {
                                  location: {
                                    ...primaryLocation,
                                  },
                                  address: {
                                    ...primaryAddress,
                                    Line3: e.target.value,
                                  },
                                },
                                true,
                                true
                              );
                            }}
                          />
                        )}
                        <Input
                          className="p-2 rounded bg-gray-50 border-0"
                          value={primaryAddress.City}
                          onChange={(e) => {
                            setPrimaryAddress({
                              ...primaryAddress,
                              City: e.target.value,
                            });
                            handleProfileValueUpdate(
                              "addresses",
                              {
                                location: {
                                  ...primaryLocation,
                                },
                                address: {
                                  ...primaryAddress,
                                  City: e.target.value,
                                },
                              },
                              true,
                              true
                            );
                          }}
                        />
                        <Input
                          className="p-2 rounded bg-gray-50 border-0"
                          value={primaryAddress.PostalCode}
                          onChange={(e) => {
                            setPrimaryAddress({
                              ...primaryAddress,
                              PostalCode: e.target.value,
                            });
                            handleProfileValueUpdate(
                              "addresses",
                              {
                                location: {
                                  ...primaryLocation,
                                },
                                address: {
                                  ...primaryAddress,
                                  PostalCode: e.target.value,
                                },
                              },
                              true,
                              true
                            );
                          }}
                        />
                      </div>
                    )}
                    <div className="pt-2">
                      <Checkbox
                        colorScheme="green"
                        size="sm"
                        isChecked={primaryDisabled}
                        onChange={(e) => {
                          handleProfileValueUpdate(
                            "disabled",
                            e.target.checked,
                            false,
                            true
                          );
                        }}
                      >
                        <p>Disabled access at location?</p>
                      </Checkbox>
                    </div>
                  </div>
                  {secondLocation ? (
                    <div className="bg-white p-2 flex flex-col rounded">
                      <div className="flex items-center gap-2 border-b pb-2 mb-2">
                        <IconLocationPin size="20px" />
                        <p className="flex-1">Secondary location</p>
                        <Tooltip hasArrow label="Remove">
                          <button
                            className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded hover:bg-gray-100 transition"
                            onClick={() => {
                              handleProfileValueUpdate(
                                "addresses",
                                null,
                                true,
                                false
                              );
                              setSecondLocation(false);
                            }}
                          >
                            <MdDelete className="w-5 h-5 text-red-500" />
                          </button>
                        </Tooltip>
                      </div>
                      <AddressSearch
                        locale="en-GB"
                        apiKey={process.env.NEXT_PUBLIC_LOQATE_APIKEY}
                        onSelect={(address) => {
                          setSecondaryAddress(address);
                          loqateSearchAddress(address, false);
                        }}
                        countries={["GB", "GG", "IE", "IM", "JE"]}
                        components={searchListComponents}
                      />
                      {secondaryAddress && (
                        <div className="flex flex-col gap-1 py-2 border-b">
                          <div className='px-2 pb-1'>
                            <span className='text-brand-alt text-sm'>
                              You can edit the text of your location below. This will not change the actual location in search. To update the location in search, please search for the address in the box above
                            </span>
                          </div>
                          <Input
                            className="p-2 rounded bg-gray-50 border-0"
                            value={secondaryAddress.Line1}
                            onChange={(e) => {
                              setSecondaryAddress({
                                ...secondaryAddress,
                                Line1: e.target.value,
                              });
                              handleProfileValueUpdate(
                                "addresses",
                                {
                                  location: {
                                    ...secondaryLocation,
                                  },
                                  address: {
                                    ...secondaryAddress,
                                    Line1: e.target.value,
                                  },
                                },
                                true,
                                false
                              );
                            }}
                          />
                          {secondaryAddress.Line2 && (
                            <Input
                              className="p-2 rounded bg-gray-50 border-0"
                              value={secondaryAddress.Line2}
                              onChange={(e) => {
                                setSecondaryAddress({
                                  ...secondaryAddress,
                                  Line2: e.target.value,
                                });
                                handleProfileValueUpdate(
                                  "addresses",
                                  {
                                    location: {
                                      ...secondaryLocation,
                                    },
                                    address: {
                                      ...secondaryAddress,
                                      Line2: e.target.value,
                                    },
                                  },
                                  true,
                                  false
                                );
                              }}
                            />
                          )}
                          {secondaryAddress.Line3 && (
                            <Input
                              className="p-2 rounded bg-gray-50 border-0"
                              value={secondaryAddress.Line3}
                              onChange={(e) => {
                                setSecondaryAddress({
                                  ...secondaryAddress,
                                  Line3: e.target.value,
                                });
                                handleProfileValueUpdate(
                                  "addresses",
                                  {
                                    location: {
                                      ...secondaryLocation,
                                    },
                                    address: {
                                      ...secondaryAddress,
                                      Line3: e.target.value,
                                    },
                                  },
                                  true,
                                  false
                                );
                              }}
                            />
                          )}
                          <Input
                            className="p-2 rounded bg-gray-50 border-0"
                            value={secondaryAddress.City}
                            onChange={(e) => {
                              setSecondaryAddress({
                                ...secondaryAddress,
                                City: e.target.value,
                              });
                              handleProfileValueUpdate(
                                "addresses",
                                {
                                  location: {
                                    ...secondaryLocation,
                                  },
                                  address: {
                                    ...secondaryAddress,
                                    City: e.target.value,
                                  },
                                },
                                true,
                                false
                              );
                            }}
                          />
                          <Input
                            className="p-2 rounded bg-gray-50 border-0"
                            value={secondaryAddress.PostalCode}
                            onChange={(e) => {
                              setSecondaryAddress({
                                ...secondaryAddress,
                                PostalCode: e.target.value,
                              });
                              handleProfileValueUpdate(
                                "addresses",
                                {
                                  location: {
                                    ...secondaryLocation,
                                  },
                                  address: {
                                    ...secondaryAddress,
                                    PostalCode: e.target.value,
                                  },
                                },
                                true,
                                false
                              );
                            }}
                          />
                        </div>
                      )}
                      <div className="pt-2">
                        <Checkbox
                          colorScheme="green"
                          size="sm"
                          isChecked={secondaryDisabled}
                          onChange={(e) => {
                            handleProfileValueUpdate(
                              "disabled",
                              e.target.checked,
                              false,
                              false
                            );
                          }}
                        >
                          <p>Disabled access at location?</p>
                        </Checkbox>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <button
                        className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded hover:bg-gray-100 transition"
                        onClick={() => setSecondLocation(true)}
                      >
                        <MdOutlineAddCircleOutline className="w-5 h-5" />
                        <p className="text-sm font-light">
                          Add second location
                        </p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Collapse>
          </div>
          <div
            className={`w-full p-4 transition rounded ${emailChecked ? "bg-green-50" : "bg-gray-100"}`}
          >
            <Checkbox
              isChecked={emailChecked}
              colorScheme="green"
              onChange={(e) => {
                handleProfileValueUpdate("sessionTypes", {
                  value: 4,
                  label: "By email",
                  selected: e.target.checked,
                });
              }}
            >
              <p
                className={`transition ${emailChecked ? "text-slate-800" : "text-slate-600"}`}
              >
                By email
              </p>
            </Checkbox>
            <Collapse in={emailChecked} animateOpacity width="100%">
              <div>
                <h5 className="font-light text-sm my-2 flex items-center text-slate-700">
                  Your contact email address will be used when you make a booking with a client.
                  <Tooltip className='cursor-pointer' hasArrow label="To change this email, please contact info@welldoing.org">
                    <InfoIcon className='cursor-pointer hover:text-blue-400 transition' color="blue.200" marginLeft="4px"/>
                  </Tooltip>
                </h5>
                <Field disabled className="flex items-center bg-white rounded overflow-hidden text-neutral-400">
                  <Label className="pl-4">
                    <IconEmail />
                  </Label>
                  <Input
                    className="flex-1 w-full appearance-none border-none cursor-not-allowed"
                    type="email"
                    placeholder="Email address"
                    disabled={true}
                    readOnly={true}
                    value={profile.contactEmail || ""}
                    aria-disabled={true}
                    aria-readonly={true}
                  />
                </Field>
              </div>
            </Collapse>
          </div>
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
};

export default EditSectionSessionType;
